import { Group, TextInput } from '@mantine/core';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import i18n from '../../../../base-dictionary';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useStrategyStore } from '../../../../store/strategy-store/strategy-store';
import {
  setActivePanel,
  setSearchTerm,
  setSelectedInitiative,
  setTreeMapMetric,
  setView,
} from '../../../../store/strategy-store/strategy-store.actions';
import { Panel, TreeMapMetric, View } from '../../../../store/strategy-store/strategy-store.type';
import { baseWhite, inkLight, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Select } from '../../../../ui-library/select/select';
import { Text } from '../../../../ui-library/typography/typography';
import { useFilteredInitiatives } from '../../strategy-v2.hooks';
import { PresentationModal } from '../presentation-modal/presentation-modal';
import { getTreeMapMetricOptions, handleDownloadCSV } from './main-section.helpers';

const isPresentationEnabled = import.meta.env.VITE_FEATURE_FLAG_PRESENTATION === 'true';

export const MainSectionControls = () => {
  const teams = useGlobalStore((state) => state.teams);
  const organization = useGlobalStore((state) => state.organization);
  const activeView = useStrategyStore((state) => state.view);
  const searchTerm = useStrategyStore((state) => state.searchTerm);
  const treeMapMetric = useStrategyStore((state) => state.treeMapMetric);
  const kpiOverviewData = useStrategyStore((state) => state.kpiOverviewData);
  const { initiatives } = useFilteredInitiatives();

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [presentationModalOpen, setPresentationModalOpen] = useState(false);

  const getButtonVariant = (view: View) => {
    return activeView === view ? 'secondary' : 'outline';
  };

  const getIconColor = (view: View) => {
    return activeView === view ? baseWhite : inkLight;
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 200),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleViewChange = (view: View) => {
    setView(view);
    setActivePanel(Panel.Filters);
    setSelectedInitiative(null);
  };

  const disableControls = initiatives.length === 0;

  return (
    <div>
      <Group justify="space-between" miw={760}>
        <Group gap={16}>
          <Group gap={8}>
            <Button
              variant={getButtonVariant(View.Table)}
              radius="xl"
              size="sm"
              leftSection={
                <Icon
                  name="format_list_bulleted"
                  color={getIconColor(View.Table)}
                  size={16}
                  style={{ cursor: 'pointer' }}
                />
              }
              onClick={() => handleViewChange(View.Table)}
            >
              {i18n.t('strategy.initiatives.table_view')}
            </Button>
            <Button
              variant={getButtonVariant(View.TreeMap)}
              radius="xl"
              size="sm"
              leftSection={
                <Icon name="border_all" color={getIconColor(View.TreeMap)} size={16} style={{ cursor: 'pointer' }} />
              }
              onClick={() => handleViewChange(View.TreeMap)}
            >
              {i18n.t('strategy.initiatives.tree_map_view')}
            </Button>
          </Group>
          {activeView === View.TreeMap && !disableControls && (
            <Group gap={8}>
              <Text size="small" lineHeight="none">
                {i18n.t('strategy.initiatives.metric')}
              </Text>
              <Select
                value={treeMapMetric}
                options={getTreeMapMetricOptions(kpiOverviewData)}
                onChange={(value) => {
                  setTreeMapMetric(value as TreeMapMetric);
                }}
                style={{ width: 240 }}
              />
            </Group>
          )}
        </Group>
        <Group>
          {activeView === View.Table && (
            <TextInput
              placeholder={i18n.t('strategy.initiatives.search_placeholder')}
              radius="xl"
              size="sm"
              value={localSearchTerm}
              leftSection={<Icon name="search" variant="filled" size={16} color={inkLight} />}
              style={{ width: 300 }}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setLocalSearchTerm(value);
                debouncedSearch(value);
              }}
            />
          )}
          <Button
            variant="secondary"
            radius="xl"
            size="sm"
            leftSection={
              <Icon
                name="download"
                color={disableControls ? skyDark : baseWhite}
                variant="filled"
                size={16}
                style={{ cursor: disableControls ? 'not-allowed' : 'pointer' }}
              />
            }
            onClick={() => handleDownloadCSV(initiatives, teams)}
            disabled={disableControls}
          >
            {i18n.t('common.actions.export')}
          </Button>
          {isPresentationEnabled && organization?.can_export_presentations && (
            <Button
              variant="secondary"
              radius="xl"
              size="sm"
              leftSection={<Icon name="slideshow" color={baseWhite} variant="filled" size={16} />}
              onClick={() => setPresentationModalOpen(true)}
            >
              {i18n.t('strategy.initiatives.create_presentation')}
            </Button>
          )}
        </Group>
      </Group>

      {isPresentationEnabled && organization?.can_export_presentations && (
        <PresentationModal
          opened={presentationModalOpen}
          onClose={() => setPresentationModalOpen(false)}
          initiativeIds={initiatives.map((initiative) => initiative.id)}
          initiativesCount={initiatives.length}
        />
      )}
    </div>
  );
};
